import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useLocation, useNavigate } from "react-router-dom";
import { assetIcons, assetImages } from "../../constants/assets";
import { fontSizes } from "../../theme/font-sizes";
import DashedButton from "../utils/dashed-button";
import englishTranslations from "../../i18n/translations/en";
import { darkColors } from "../../theme/colors";
import ConfirmationModal from "../utils/popups/confirmation-popup";
import WalletPopup from "../utils/popups/wallet-popup";
import { useProfileVisibility } from "../../context/profile-visibility-context";
import { UserProfile } from "../utils/home/user-profile";
import MobileSocialAccountBar from "../utils/home/mobile-social-bar";
import { ChatSection } from "../utils/home/chat-section";
import PlanetXDescription from "../utils/footer-text";
import routes from "../../constants/routes";
import { useUser } from "../../context/user-context";
import InlineSvg from "../utils/inline-svg";
import useApiRequest from "../../api/api";
import GradientButton from "../utils/gradient-button";
import WalletConnector from "../utils/wallet-connector";
import TermsPrivacyLinks from "../utils/terms-privacy-links";

const Navbar = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { user, updateUser } = useUser();

  const { showProfile, toggleProfileVisibility } = useProfileVisibility();

  const location = useLocation();

  const navigate = useNavigate();
  const handleSettingsClick = () => {
    toggleProfileVisibility();
    navigate(routes.settings);
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };

  // To close the modal
  const handleClose = () => {
    setOpen(false);
  };
  const handleNavigateSettings = () => {
    navigate(routes.settings);
  };

  const handleNavigateHome = () => {
    if (location.pathname === "/race") {
      // If on Race page and navigating to Home, show confirmation modal
      handleOpenGameModal(); // Open the modal
    } else {
      // Navigate directly for other cases
      navigate(routes.home);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem("user");
    updateUser(null); // Add your confirmation logic here (e.g., logout)
    handleClose(); // Close modal after confirming
    navigate(routes.login);
  };
  const handleIconClick = (url) => {
    window.open(url, "_blank");
    // Add additional logic for each icon as needed
  };
  const [openGameModal, setOpenGameModal] = useState(false);

  const handleOpenGameModal = () => {
    setOpenGameModal(true);
  };
  const closeGameModal = () => {
    setOpenGameModal(false);
  };
  const navigateToMainMenu = () => {
    closeGameModal();
    navigate(routes.home);
  };

  const handleLogin = () => {
    navigate(routes.login);
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: `linear-gradient(to right, ${darkColors.backgroundTop} 0%, ${darkColors.navbarGray} 50%, ${darkColors.backgroundTop} 100%)`,
          height: isMediumScreen ? "58px" : "80px",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mx: "0%",
            mt: isMediumScreen ? 0 : 1,
          }}
        >
          <Box display={"flex"}>
            <IconButton color="inherit" onClick={handleNavigateHome}>
              <Box
                alt="User Avatar"
                component="img"
                src={assetImages.logo}
                sx={{
                  width: isMediumScreen ? "24px" : "54px",
                  height: isMediumScreen ? "24px" : "42px",
                }}
              />
            </IconButton>
            <Typography
              onClick={handleNavigateHome}
              fontSize={
                isMediumScreen ? fontSizes.heading3 : fontSizes.heading2
              }
              fontWeight={600}
              mt={isMediumScreen ? 1 : 1.5}
              ml={isMediumScreen ? 0 : 1}
              component="div"
              sx={{
                cursor: "pointer",
                color: "white",
              }}
            >
              {englishTranslations.auth.planetJ}
            </Typography>
          </Box>

          <IconButton
            // onClick={handleNavigateProfile}
            sx={{
              "&:hover": {
                backgroundColor: "transparent", // Removes hover background
              },
              padding: 0, // Optional: removes padding around the Avatar if desired
            }}
            disableRipple
            color="inherit"
          >
            {!user?.wallet && (
              <WalletConnector>
                {({ handleOpenModal }) => (
                  <DashedButton
                    icon={assetIcons.digitalWallet}
                    text={
                      isSmallScreen
                        ? ""
                        : englishTranslations.auth.connectWallet
                    }
                    onClick={handleOpenModal} // Use handleOpenModal here
                    width={
                      isMediumScreen
                        ? isSmallScreen
                          ? "auto"
                          : "150px"
                        : "200px"
                    }
                    padding={isMediumScreen ? 4 : "10px 10px"}
                  />
                )}
              </WalletConnector>
              // <DashedButton
              //   icon={assetIcons.digitalWallet}
              //   text={englishTranslations.auth.connectWallet}
              //   onClick={hanldeWalletOpen}
              //   width={isMediumScreen ? "150px" : "200px"}
              //   padding={isMediumScreen ? 4 : "10px 10px"}
              // />
            )}
            <Box ml={isMediumScreen ? 1 : 1.5}></Box>
            {user?.role === "Guest" && (
              <GradientButton
                text={"Login"}
                onClick={handleLogin}
                width={isMediumScreen ? "80px" : "100px"}
                py={isMediumScreen ? 0.8 : 1.45}
              />
            )}
            {!isMediumScreen && user?.role !== "Guest" && (
              <>
                <Box
                  onClick={handleNavigateSettings}
                  ml={1.5}
                  component={"img"}
                  src={assetIcons.settings}
                  alt="settings"
                ></Box>
                <Box
                  onClick={handleOpen}
                  ml={1.5}
                  component={"img"}
                  src={assetIcons.logout}
                  alt="logout"
                ></Box>
              </>
            )}
          </IconButton>

          {isMediumScreen && user?.role !== "Guest" && (
            <Box
              component={"img"}
              sx={{
                width: isMediumScreen ? "20px" : "auto",
                height: isMediumScreen ? "20px" : "auto",
              }}
              mr={1}
              onClick={toggleProfileVisibility}
              src={assetImages.navbarProfileAvatar}
              alt="..."
            />
          )}
        </Toolbar>
      </AppBar>
      <ConfirmationModal
        open={open}
        onClose={handleClose}
        img={assetImages.logoutImage} // Dynamic image source
        title="Logout From Account"
        message="Are you sure you want to logout?"
        confirmButtonText="LOGOUT"
        closeButtonText="CANCEL"
        handleSubmit={handleLogout} // Action to be executed on confirm
        secondaryClick={handleClose}
      />
      <ConfirmationModal
        open={openGameModal}
        onClose={closeGameModal}
        img={assetImages.warning} // Dynamic image source
        title="Game Still In Progress"
        message="Are you sure you want to leave?"
        confirmButtonText="BACK TO GAME"
        closeButtonText="GO TO MAIN MENU"
        handleSubmit={closeGameModal} // Action to be executed on confirm
        secondaryClick={navigateToMainMenu}
      />

      {showProfile && (
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            bgcolor: darkColors.background,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: isMediumScreen ? 2.5 : "15px",
              pt: "38px",
              pl: isMediumScreen ? "auto" : "38px",
              height: "auto",
              mb: 2,
              // mt: 4,
              "&::-webkit-scrollbar": {
                display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
              },
            }}
          >
            <Box
              sx={{
                display: "flex", // Enable flexbox
                flexDirection: "column", // Stack children vertically
                minHeight: "100vh", // Ensure the parent Box takes up the full viewport height
                width: "100%",
                width: "100%",
                p: 0,
                height: "auto",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
                },
                mb: 10,
              }}
            >
              <Box
                onClick={toggleProfileVisibility}
                display="flex"
                width={0}
                alignItems="center"
                mb={4}
                gap={2.5}
              >
                <InlineSvg
                  sx={{
                    width: 20,
                  }}
                  url={assetIcons.leftArrow}
                />
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading2}
                  component="span"
                  color="white"
                  onClick={(e) => e.stopPropagation()}
                >
                  Back
                </Typography>
              </Box>
              <UserProfile
                username="@ExHuma64"
                avatarSrc="/placeholder.svg?height=40&width=40"
                balance={231}
              />
              <Box mt={3}></Box>
              <ChatSection />
              <Box sx={{ mt: "auto", pt: isMediumScreen ? 0 : 4 }}>
                <MobileSocialAccountBar
                  text={englishTranslations.auth.noAccount}
                  onIconClick={handleIconClick}
                  onLogoutClick={handleOpen}
                  onSettingsClick={handleSettingsClick}
                />
                <Divider sx={{ my: 2, background: darkColors.gray }} />
                <PlanetXDescription isMobile={isMediumScreen} />
                <TermsPrivacyLinks />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Navbar;
